import { useState, useMemo, useEffect, useCallback } from "react";
import { useData } from "@/context/data-provider";
import { planKey } from "@/lib/constants";
import { dbGet, dbKeys } from "@/lib/db";
import type { DayPlan } from "@/types";

const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const MONTH_NAMES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

function getMonthDays(year: number, month: number) {
  const first = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0).getDate();
  let startDay = first.getDay() - 1;
  if (startDay < 0) startDay = 6;
  const days: (number | null)[] = Array(startDay).fill(null);
  for (let i = 1; i <= lastDay; i++) days.push(i);
  return days;
}

function fmt(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export default function Agenda() {
  const {
    doctors: { doctors },
    visits: { visits },
    plans: { getPlansForDate, addPlanForDate, togglePlanForDate, removePlanForDate },
  } = useData();

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(fmt(today.getFullYear(), today.getMonth(), today.getDate()));
  const [dayPlans, setDayPlans] = useState<DayPlan[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [entryType, setEntryType] = useState("local");
  const [searchDoc, setSearchDoc] = useState("");
  const [selDocId, setSelDocId] = useState<string | null>(null);
  const [acOpen, setAcOpen] = useState(false);
  const [planTime, setPlanTime] = useState("");
  const [planNote, setPlanNote] = useState("");

  const ENTRY_TYPES = [
    { value: "local", label: "Local", color: "#1B4F9B" },
    { value: "medico", label: "Médico", color: "#1B4F9B" },
    { value: "feriado", label: "Feriado", color: "#C0392B" },
    { value: "ponte", label: "Ponte", color: "#C0392B" },
    { value: "ferias", label: "Férias", color: "#C0392B" },
    { value: "reuniao_ciclo", label: "Reunião de Ciclo", color: "#6C3483" },
    { value: "reuniao_equipa", label: "Reunião de Equipa", color: "#6C3483" },
    { value: "formacao", label: "Formação", color: "#0E7979" },
  ] as const;

  const typeColor = (doctorId: string) => {
    if (!doctorId.startsWith("_")) return "#1B4F9B"; // medico
    const type = doctorId.slice(1);
    return ENTRY_TYPES.find((t) => t.value === type)?.color ?? "#1B4F9B";
  };

  const days = useMemo(() => getMonthDays(year, month), [year, month]);
  const todayStr = fmt(today.getFullYear(), today.getMonth(), today.getDate());

  // Load all plans for the visible month
  const [monthPlans, setMonthPlans] = useState<Record<string, DayPlan[]>>({});

  useEffect(() => {
    (async () => {
      const prefix = `mv5_plan_${year}-${String(month + 1).padStart(2, "0")}`;
      const allKeys = await dbKeys();
      const relevantKeys = allKeys.filter((k) => k.startsWith(prefix));
      const result: Record<string, DayPlan[]> = {};
      for (const k of relevantKeys) {
        const date = k.replace("mv5_plan_", "");
        const plans = await dbGet<DayPlan[]>(k);
        if (plans?.length) result[date] = plans;
      }
      setMonthPlans(result);
    })();
  }, [year, month, dayPlans]);

  const loadDayPlans = useCallback(async (date: string) => {
    const plans = await getPlansForDate(date);
    setDayPlans(plans);
  }, [getPlansForDate]);

  useEffect(() => {
    if (selectedDate) loadDayPlans(selectedDate);
  }, [selectedDate, loadDayPlans]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };

  const dayVisits = selectedDate ? (visits[selectedDate] ?? []) : [];
  const isFullDayType = entryType !== "medico" && entryType !== "local";
  const hasFullDayEvent = dayPlans.some((p) => p.doctorId.startsWith("_") && p.doctorId !== "_local");

  const acResults = useMemo(() => {
    if (!searchDoc.trim()) return [];
    const q = searchDoc.toLowerCase();
    return doctors.filter((d) => d.name.toLowerCase().includes(q) || d.local.toLowerCase().includes(q)).slice(0, 6);
  }, [searchDoc, doctors]);

  const handleAddPlan = async () => {
    if (!selectedDate) return;
    let plan: DayPlan;
    if (entryType === "medico") {
      const doc = doctors.find((d) => d.id === selDocId);
      if (!doc) return;
      plan = { doctorId: doc.id, name: doc.name, time: planTime, note: planNote, done: false, local: doc.local };
    } else {
      const label = ENTRY_TYPES.find((t) => t.value === entryType)?.label ?? entryType;
      plan = { doctorId: `_${entryType}`, name: label, time: planTime, note: planNote, done: false, local: "" };
    }
    await addPlanForDate(selectedDate, plan);
    setDayPlans((prev) => [...prev, plan]);
    setSearchDoc(""); setSelDocId(null); setPlanTime(""); setPlanNote(""); setEntryType("medico"); setShowAdd(false);
  };

  const handleToggle = async (doctorId: string) => {
    if (!selectedDate) return;
    await togglePlanForDate(selectedDate, doctorId);
    setDayPlans((prev) => prev.map((p) => (p.doctorId === doctorId ? { ...p, done: !p.done } : p)));
  };

  const handleRemove = async (doctorId: string) => {
    if (!selectedDate) return;
    await removePlanForDate(selectedDate, doctorId);
    setDayPlans((prev) => prev.filter((p) => p.doctorId !== doctorId));
  };

  return (
    <div className="container">
      <h1 className="font-serif text-[1.3rem] mb-4" style={{ color: "var(--mv-ink-dark)" }}>Agenda</h1>

      {/* Calendar */}
      <div className="mv-panel mb-4">
        <div className="mv-panel-header justify-between">
          <button className="mv-btn mv-btn-ghost mv-btn-sm" onClick={prevMonth}>◀</button>
          <span className="mv-panel-title">{MONTH_NAMES[month]} {year}</span>
          <button className="mv-btn mv-btn-ghost mv-btn-sm" onClick={nextMonth}>▶</button>
        </div>
        <div className="p-[10px]">
          <div className="cal-grid mb-1">
            {WEEKDAYS.map((d) => (
              <div key={d} className="font-mono text-[0.58rem] uppercase tracking-wider text-center py-1" style={{ color: "var(--mv-ink-light)" }}>{d}</div>
            ))}
          </div>
          <div className="cal-grid">
            {days.map((day, i) => {
              if (day === null) return <div key={`e${i}`} />;
              const dateStr = fmt(year, month, day);
              const datePlans = monthPlans[dateStr] ?? [];
              const hasVisits = visits[dateStr]?.length > 0;
              const isToday = dateStr === todayStr;
              const isSelected = dateStr === selectedDate;
              // Deduplicate colors
              const dotColors = [...new Set(datePlans.map((p) => typeColor(p.doctorId)))];
              if (hasVisits && !dotColors.includes("#4A9E2F")) dotColors.push("#4A9E2F");
              return (
                <div
                  key={i}
                  className={`cal-day ${isToday ? "today" : ""} ${isSelected ? "selected" : ""}`}
                  onClick={() => setSelectedDate(dateStr)}
                >
                  <span>{day}</span>
                  <div className="flex gap-[2px] justify-center min-h-[6px] flex-wrap">
                    {dotColors.map((c) => (
                      <span key={c} className="w-[5px] h-[5px] rounded-full" style={{ background: c }} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Day detail */}
      {selectedDate && (
        <div className="mv-panel">
          <div className="mv-panel-header justify-between">
            <span className="mv-panel-title">{selectedDate}</span>
            <button className="mv-btn mv-btn-primary mv-btn-sm" onClick={() => setShowAdd(!showAdd)}>+ Planear</button>
          </div>

          {showAdd && (
            <div className="p-[14px_18px]" style={{ borderBottom: "1px solid var(--mv-border)", background: "var(--mv-surface3)" }}>
              <div className="mb-[9px]">
                <label className="mv-label">Tipo</label>
                <select className="mv-select" value={entryType} onChange={(e) => { setEntryType(e.target.value); setSelDocId(null); setSearchDoc(""); }}>
                  {ENTRY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {entryType === "medico" && (
                <div className="relative mb-[9px]">
                  <input
                    className="mv-input" placeholder="Pesquisar médico..."
                    value={searchDoc}
                    onChange={(e) => { setSearchDoc(e.target.value); setSelDocId(null); setAcOpen(true); }}
                    onFocus={() => setAcOpen(true)}
                    onBlur={() => setTimeout(() => setAcOpen(false), 200)}
                  />
                  {acOpen && acResults.length > 0 && (
                    <div className="absolute top-[calc(100%+3px)] left-0 right-0 z-[200] rounded-[9px] max-h-[200px] overflow-y-auto" style={{ background: "var(--mv-surface)", border: "1.5px solid var(--mv-blue)", boxShadow: "0 8px 24px var(--mv-shadow)" }}>
                      {acResults.map((d) => (
                        <div key={d.id} className="p-[9px_12px] cursor-pointer flex items-center justify-between gap-2 hover:bg-[var(--mv-blue-light)]" style={{ borderBottom: "1px solid var(--mv-border)" }} onMouseDown={() => { setSelDocId(d.id); setSearchDoc(d.name); setAcOpen(false); }}>
                          <div><div className="font-semibold text-[0.82rem]">{d.name}</div><div className="text-[0.67rem]" style={{ color: "var(--mv-ink-light)" }}>{d.local}</div></div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {entryType === "local" && (
                <div className="mb-[9px]">
                  <label className="mv-label">Local</label>
                  <input className="mv-input" value={planNote} onChange={(e) => setPlanNote(e.target.value)} placeholder="Local..." />
                </div>
              )}
              {(entryType === "medico" || entryType === "local") && (
                <div className="mb-[9px]" style={{ maxWidth: 140 }}>
                  <label className="mv-label">Hora</label>
                  <input type="time" className="mv-input" value={planTime} onChange={(e) => setPlanTime(e.target.value)} />
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button className="mv-btn mv-btn-ghost mv-btn-sm" onClick={() => setShowAdd(false)}>Cancelar</button>
                <button className="mv-btn mv-btn-primary mv-btn-sm" onClick={handleAddPlan} disabled={isFullDayType && hasFullDayEvent || (entryType === "medico" && !selDocId)}>Adicionar</button>
              </div>
            </div>
          )}

          <div className="p-[14px]">
            {dayPlans.length > 0 && (
              <div className="mb-3">
                <div className="section-title">Planeado</div>
                {dayPlans.map((p, i) => (
                  <div key={i} className={`plan-item ${p.done ? "completed" : ""}`}>
                    <button
                      className={`plan-check ${p.done ? "checked" : ""}`}
                      onClick={() => handleToggle(p.doctorId)}
                    >
                      {p.done ? "✓" : ""}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-[0.82rem] font-semibold" style={{ color: "var(--mv-ink-dark)", textDecoration: p.done ? "line-through" : undefined }}>{p.name}</div>
                      <div className="text-[0.68rem]" style={{ color: "var(--mv-ink-light)" }}>{p.local}</div>
                      {p.note && <div className="text-[0.68rem] mt-[2px]">{p.note}</div>}
                    </div>
                    {p.time && (
                      <span className="font-mono text-[0.6rem]" style={{ color: "var(--mv-orange)" }}>⏰ {p.time}</span>
                    )}
                    <button className="mv-btn mv-btn-ghost mv-btn-sm" style={{ padding: "2px 6px", fontSize: "0.9rem" }} onClick={() => handleRemove(p.doctorId)}>×</button>
                  </div>
                ))}
              </div>
            )}
            {dayVisits.length > 0 && (
              <div>
                <div className="section-title">Visitas realizadas</div>
                {dayVisits.map((v, i) => (
                  <div key={i} className="py-[6px]">
                    <span className="font-semibold text-[0.82rem]">{v.name}</span>
                    <span className="font-mono text-[0.6rem] ml-2" style={{ color: "var(--mv-ink-light)" }}>{v.time}</span>
                    {v.products.length > 0 && (
                      <div className="flex gap-1 mt-1">{v.products.map((p) => <span key={p} className="prod-tag">{p}</span>)}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {dayPlans.length === 0 && dayVisits.length === 0 && (
              <div className="empty-state">Sem atividade neste dia</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
